import React from "react";
import type { SiteManifest, PublishedPage, ManifestPageEntry } from "../types";
import { ReadOnlyContent } from "./ReadOnlyContent";
import {
  transformHtmlEmbeds,
  attachHtmlEmbedFullscreen,
} from "./transform-html-embeds";

interface PageViewProps {
  page: PublishedPage;
  manifest: SiteManifest;
  onNavigate: (slug: string | null) => void;
}

export const PageView: React.FC<PageViewProps> = ({
  page,
  manifest,
  onNavigate,
}) => {
  const { showLastUpdated, showRelatedPages } = manifest.space.layout;

  // Resolve related pages from the manifest. The manifest only contains
  // public/published/non-archived pages, so any related id pointing at a
  // draft / private / archived / deleted page silently drops out here —
  // no server-side filter required.
  //
  // The manifest emits one entry per (page, folder) membership so we dedupe
  // by uuid to avoid rendering the same related card twice.
  const relatedIds = page.related_page_ids ?? [];
  const relatedPages: ManifestPageEntry[] = (() => {
    if (!showRelatedPages || relatedIds.length === 0) return [];
    const wanted = new Set(relatedIds);
    const seen = new Set<string>();
    const out: ManifestPageEntry[] = [];
    for (const entry of manifest.pages) {
      if (!wanted.has(entry.uuid) || seen.has(entry.uuid)) continue;
      seen.add(entry.uuid);
      out.push(entry);
    }
    return out;
  })();

  const hasRelated = relatedPages.length > 0;
  const hasLastUpdated = Boolean(showLastUpdated && page.last_edited_at);

  // Wire up the delegated fullscreen click handler for HTML embeds rendered
  // via the dangerouslySetInnerHTML path (the <script> tag approach doesn't
  // work because innerHTML-injected scripts are never executed by browsers).
  React.useEffect(() => {
    if (page.tiptapContent) return;
    return attachHtmlEmbedFullscreen();
  }, [page.tiptapContent, page.htmlContent]);

  return (
    <article className="w-full">
      <header className="mb-6">
        <h1 className="text-2xl !font-semibold !font-sans tracking-tight mb-2 text-foreground flex items-center gap-2">
          <span>{page.title}</span>
        </h1>
        {page.description && (
          <p className="text-base text-muted-foreground">{page.description}</p>
        )}
      </header>

      {page.tiptapContent ? (
        <ReadOnlyContent content={page.tiptapContent} />
      ) : (
        <div
          className="help-article-content prose prose-sm max-w-none text-[15px] leading-relaxed dark:prose-invert"
          dangerouslySetInnerHTML={{ __html: transformHtmlEmbeds(page.htmlContent) }}
        />
      )}

      {/*
        Single page-footer that merges "Related pages" + "Updated …" into one
        bordered block. Header row is laid out as [heading | updated date]
        so the two pieces of metadata sit side-by-side instead of stacking
        their own border-t lines.
        Render only when at least one piece is present; otherwise nothing.
      */}
      {(hasRelated || hasLastUpdated) && (
        <footer
          aria-labelledby={hasRelated ? "related-pages-heading" : undefined}
          className="mt-10 pt-6 border-t border-border"
        >
          <div
            className={`flex items-center justify-between gap-4 ${
              hasRelated ? "mb-3" : ""
            }`}
          >
            {hasRelated ? (
              <h2
                id="related-pages-heading"
                className="text-xs !font-semibold !font-sans uppercase tracking-wide text-muted-foreground"
              >
                Related pages
              </h2>
            ) : (
              <span aria-hidden />
            )}
            {hasLastUpdated && (
              <p className="text-xs text-muted-foreground shrink-0">
                Updated{" "}
                {new Date(page.last_edited_at).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
            )}
          </div>

          {hasRelated && (
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {relatedPages.map((rp) => (
                <li key={rp.uuid}>
                  <a
                    href={`/${rp.slug}`}
                    onClick={(e) => {
                      e.preventDefault();
                      onNavigate(rp.slug);
                    }}
                    className="group block p-4 rounded-lg border border-border/60 hover:border-border hover:bg-muted/30 transition-colors"
                  >
                    <div className="text-sm font-medium text-foreground flex items-center gap-2">
                      {rp.emoji && (
                        <span aria-hidden className="shrink-0">
                          {rp.emoji}
                        </span>
                      )}
                      <span className="truncate">{rp.title}</span>
                    </div>
                  </a>
                </li>
              ))}
            </ul>
          )}
        </footer>
      )}
    </article>
  );
};
